import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import ErrorBanner from '../../components/shared/ErrorBanner'
import { setAlert } from '../../features/appSlice'
import {
    cancelFailureMessage,
    cancelPayload,
    cancelSuccessToast,
    parseScheduledSends,
    statusLabel,
} from '../../helpers/scheduledSends'
import {
    INDIA_ZONE,
    formatIndiaTime,
    formatInZone,
} from '../../helpers/sendSchedule'
import { AlertType } from '../../helpers/Constants'
import useCommsManager from '../../hooks/useCommsManager'
import BulkSendService from '../../services/bulkSendService'
import { errorMessage } from '../../services/axios/errorMessage'
import { BRAND, FONT, RADIUS, SURFACE, TEXT, cardSx } from '../../theme/tokens'
import { nowUnix } from '../../utils/datetime'
import EventPicker from './EventPicker'
import Overline from './Overline'

const EMPTY = { loading: false, error: '', sends: [] }

// Read by screen readers, not shown: the action column's header name.
const visuallyHidden = {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
}

/**
 * "Scheduled sends": pick an event type, see its sends still to go out (time
 * in India time, who created it, status, Report ID) and cancel one after a
 * confirmation. The list is reloaded after every cancel, whatever the
 * answer, since a partial cancel changes it too.
 */
const ScheduledSends = ({ eventTypes }) => {
    const dispatch = useDispatch()
    // Cancelling needs the comms manager role, as in the old app.
    const { note: manageNote } = useCommsManager()
    const [eventType, setEventType] = useState('')
    const [list, setList] = useState(EMPTY)
    const [confirming, setConfirming] = useState(null)
    const [cancellingId, setCancellingId] = useState('')
    // The event type whose answer may land; an older request's answer is dropped.
    const current = useRef('')

    const load = useCallback(async (type) => {
        current.current = type
        if (!type) {
            setList(EMPTY)
            return
        }
        setList({ loading: true, error: '', sends: [] })
        try {
            const res = await BulkSendService.scheduledSends(type)
            if (current.current !== type) return
            setList({
                loading: false,
                error: '',
                sends: parseScheduledSends(res.data, {
                    eventType: type,
                    nowSeconds: nowUnix(),
                }),
            })
        } catch (err) {
            if (current.current !== type) return
            setList({
                loading: false,
                error: errorMessage(err.response),
                sends: [],
            })
        }
    }, [])

    useEffect(() => {
        load(eventType)
    }, [eventType, load])

    const cancel = async (send) => {
        setConfirming(null)
        setCancellingId(send.eventId)
        try {
            const res = await BulkSendService.cancelScheduledSend(
                cancelPayload(send)
            )
            const toast = cancelSuccessToast(res.data, send)
            dispatch(
                setAlert({
                    open: true,
                    message: toast.message,
                    type:
                        toast.type === 'warning'
                            ? AlertType.warning
                            : AlertType.success,
                })
            )
        } catch (err) {
            dispatch(
                setAlert({
                    open: true,
                    message: cancelFailureMessage(err.response),
                    type: AlertType.error,
                })
            )
        } finally {
            setCancellingId('')
            load(send.eventType || eventType)
        }
    }

    let body = null
    if (!eventType)
        body = (
            <Typography variant="body2" sx={{ color: TEXT.faint }}>
                Pick an event type to see its scheduled sends.
            </Typography>
        )
    else if (list.loading)
        body = (
            <Box
                role="status"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
                <CircularProgress size={16} sx={{ color: BRAND.orange }} />
                <Typography variant="body2" sx={{ color: TEXT.muted }}>
                    Loading scheduled sends…
                </Typography>
            </Box>
        )
    else if (list.error)
        body = (
            <Box>
                <ErrorBanner
                    message={`Could not load the scheduled sends: ${list.error}`}
                />
                <Button
                    variant="outlined"
                    sx={{ mt: 1.5 }}
                    onClick={() => load(eventType)}
                >
                    Try again
                </Button>
            </Box>
        )
    else if (!list.sends.length)
        body = (
            <Typography variant="body2" sx={{ color: TEXT.faint }}>
                No scheduled sends for {eventType}.
            </Typography>
        )
    else
        body = (
            <TableContainer
                sx={{
                    overflowX: 'auto',
                    borderRadius: `${RADIUS.sm}px`,
                    border: `1px solid ${SURFACE.border}`,
                }}
            >
                <Table size="small" sx={{ minWidth: 640 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Sends at (India time)</TableCell>
                            <TableCell>Created by</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Report ID</TableCell>
                            <TableCell align="right">
                                <Box component="span" sx={visuallyHidden}>
                                    Action
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {list.sends.map((send) => (
                            <TableRow key={send.eventId || send.eventTime}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {formatInZone(send.eventTime, INDIA_ZONE)}
                                </TableCell>
                                <TableCell>
                                    {send.createdBy || 'Not recorded'}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {statusLabel(send.status)}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontFamily: FONT.mono,
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {send.eventId}
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title={manageNote}>
                                        {/* A disabled button gets no hover; the span does. */}
                                        <span>
                                            <Button
                                                variant="outlined"
                                                disabled={
                                                    !!cancellingId ||
                                                    Boolean(manageNote)
                                                }
                                                onClick={() =>
                                                    setConfirming(send)
                                                }
                                                aria-label={`Cancel the send at ${formatIndiaTime(
                                                    send.eventTime
                                                )}`}
                                            >
                                                {cancellingId === send.eventId
                                                    ? 'Cancelling…'
                                                    : 'Cancel'}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        )

    return (
        <Paper
            elevation={0}
            sx={{ ...cardSx, p: 4, mt: 4 }}
            data-testid="scheduled-sends"
        >
            <Overline component="h2" sx={{ mb: 0.5 }}>
                Scheduled sends
            </Overline>
            <Typography variant="body2" sx={{ color: TEXT.faint, mb: 2 }}>
                Sends of an event set for a later time. Cancel one to stop it
                before it goes out.
            </Typography>
            <Box sx={{ maxWidth: 420, mb: 2 }}>
                <EventPicker
                    id="scheduled-sends-event-type"
                    label="Event type of the scheduled sends"
                    eventTypes={eventTypes}
                    value={eventType}
                    onChange={setEventType}
                />
            </Box>
            {body}
            <ConfirmDialog
                open={!!confirming}
                title="Cancel this scheduled send?"
                message={
                    confirming
                        ? `The ${
                              confirming.eventType
                          } send scheduled for ${formatIndiaTime(
                              confirming.eventTime
                          )} (Report ID ${
                              confirming.eventId
                          }) will not go out. This cannot be undone.`
                        : ''
                }
                confirmLabel="Cancel the send"
                cancelLabel="Keep it"
                destructive
                onConfirm={() => cancel(confirming)}
                onCancel={() => setConfirming(null)}
            />
        </Paper>
    )
}

ScheduledSends.propTypes = {
    eventTypes: PropTypes.arrayOf(PropTypes.string),
}

export default ScheduledSends
